import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/features/auth/AuthProvider";
import { ValidationError } from "@/api/errors";
import { useRouteMeta } from "@/lib/seo";
import { USE_MOCK_API } from "@/mocks/guard";
import { ClubLogo } from "@/components/brand/ClubLogo";

export function LoginPage() {
  useRouteMeta({
    title: "Sign in — Soweto Super United FC",
    description: "Sign in to access your Soweto Super United FC portal.",
    noindex: true,
  });
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") ?? "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const user = await login(email, password, remember);
      const dest = decodeURIComponent(next);
      if (dest && dest !== "/") navigate(dest, { replace: true });
      else {
        const target =
          user.roles.includes("super_administrator") || user.roles.includes("club_administrator")
            ? "/admin"
            : user.roles.includes("head_coach") || user.roles.includes("assistant_coach")
              ? "/coach"
              : user.roles.includes("player")
                ? "/player"
                : "/";
        navigate(target, { replace: true });
      }
    } catch (err) {
      if (err instanceof ValidationError) setError(err.message);
      else if (err instanceof Error) setError(err.message);
      else setError("Sign in failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const quickLogin = async (localPart: string) => {
    setEmail(`${localPart}@sample.local`);
    setPassword("demo-password");
    setSubmitting(true);
    try {
      const user = await login(`${localPart}@sample.local`, "demo-password");
      const target = user.roles.includes("super_administrator")
        ? "/admin"
        : user.roles.includes("head_coach")
          ? "/coach"
          : user.roles.includes("player")
            ? "/player"
            : "/admin";
      navigate(target, { replace: true });
    } catch {
      setError("Sign in failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-8 shadow-editorial">
      <ClubLogo variant="full" className="mx-auto mb-6 max-h-24" priority />
      <h1 className="text-display text-3xl uppercase text-navy">Sign in</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Access your Soweto Super United FC portal.
      </p>

      {USE_MOCK_API ? (
        <div className="mt-5 rounded-sm border border-gold/40 bg-gold/10 p-3 text-xs text-navy">
          <p className="font-bold uppercase tracking-[0.16em]">Development mock</p>
          <p className="mt-1">Any email + password works. Quick sign in:</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {[
              ["admin", "Admin"],
              ["coach", "Coach"],
              ["player", "Player"],
              ["matchops", "Match Ops"],
            ].map(([lp, label]) => (
              <button
                key={lp}
                type="button"
                onClick={() => quickLogin(lp)}
                className="rounded-sm border border-navy/30 bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-navy hover:bg-navy hover:text-white"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <form className="mt-6 grid gap-4" onSubmit={submit} noValidate>
        <label className="grid gap-1 text-sm">
          <span className="text-xs font-semibold uppercase tracking-[0.14em]">Email</span>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="min-h-11 rounded-sm border border-input px-3"
          />
        </label>
        <label className="grid gap-1 text-sm">
          <span className="text-xs font-semibold uppercase tracking-[0.14em]">Password</span>
          <input
            id="login-password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="min-h-11 rounded-sm border border-input px-3"
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="size-4"
          />{" "}
          Remember me on this device
        </label>
        {error ? (
          <p role="alert" className="rounded-sm bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={submitting}
          className="min-h-11 rounded-sm bg-navy px-4 text-sm font-bold uppercase tracking-[0.18em] text-white hover:bg-navy-deep disabled:opacity-60"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>
        <div className="flex items-center justify-between text-sm">
          <Link to="/auth/forgot-password" className="text-navy hover:underline">
            Forgot password?
          </Link>
          <Link to="/" className="text-muted-foreground hover:underline">
            Back to site
          </Link>
        </div>
      </form>
    </div>
  );
}
